import { type AxiosError, isAxiosError } from 'axios'

export const NETWORK_ERROR_MESSAGE =
  'Unable to reach the server. Check your connection and try again.'

/** Returns true when the error is an Axios network-level failure (no response received). */
export function isNetworkError(err: unknown): err is AxiosError {
  if (!isAxiosError(err)) return false
  return (
    err.code === 'ERR_NETWORK' ||
    (!err.response && err.message === 'Network Error')
  )
}

/** Extract a human-readable message from an unknown error value. */
function errorToString(err: unknown): string {
  if (typeof err === 'string') return err
  if (err instanceof Error) return err.message
  if (err != null && typeof err === 'object') {
    // Prefer .message, then .error, then JSON.stringify
    const obj = err as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
    return JSON.stringify(err)
  }
  return String(err)
}

export function extractQueryErrors(result: {
  error?: unknown
  data?: any
  isError?: boolean
}): string[] | null {
  const errors = new Set<string>()

  if (result.error) {
    if (isNetworkError(result.error)) {
      errors.add(NETWORK_ERROR_MESSAGE)
    } else if (isAxiosError(result.error)) {
      const responseData = result.error.response?.data
      const message =
        typeof responseData === 'string'
          ? responseData
          : responseData?.message ?? responseData?.error ?? result.error.message
      if (message)
        errors.add(
          typeof message === 'string' ? message : JSON.stringify(message),
        )
    } else {
      errors.add(errorToString(result.error))
    }
  }
  if (result.data?.error) errors.add(errorToString(result.data.error))

  return result.isError || errors.size > 0 ? [...errors] : null
}

/** Combine errors from multiple query results. Returns null when there are no errors. */
export function combineQueryErrors(
  results: Parameters<typeof extractQueryErrors>[0][],
): string[] | null {
  const errors = results.flatMap((r) => extractQueryErrors(r) ?? [])
  return errors.length > 0 ? errors : null
}
